import { graphql } from 'gatsby'
import React from 'react'

import Link from '@/components/link/link'
import { generateUrl } from '@/internal/utils/generate-url'
import { getModelMessages } from '@/utils/get-model-messages'

import * as styles from './card-inline.module.scss'

export type CardsInline =
  | Queries.PctDialogCardInlineFragment
  | Queries.PctTopicCardInlineFragment
  | Queries.PctPaperCardInlineFragment
  | Queries.PctInternalPageCardInlineFragment
  | Queries.PctPostCardInlineFragment
  | Queries.PctInterviewCardInlineFragment
  | Queries.PctProjectCardInlineFragment
  | Queries.PctPageCardInlineFragment
  | Queries.PctVisionCardInlineFragment
  | Queries.PctTagCardInlineFragment
  | Queries.PctReportCardInlineFragment
  | Queries.PctReportPersonInlineFragment
  | Queries.PctEpisodeCardInlineFragment

export type CardInlineProps = {
  record: CardsInline
}

const CardInline: React.FC<CardInlineProps> = ({ record }) => {
  const modelMessages = getModelMessages(record?.__typename)
  const slug = getSlug(record)
  const url = generateUrl(record.__typename, slug)
  const topic = getTopic(record)
  const externalName = getExternalName(record)
  const headline = getHeadline(record)
  return (
    <div className={styles.cardInline}>
      <div className={styles.topics}>
        {topic !== undefined && (
          <div>
            <Link to={generateUrl('DatoCmsPctTopic', topic?.slug)}>
              {topic?.externalName}
            </Link>
          </div>
        )}
      </div>
      <h3>{externalName}</h3>
      {headline !== undefined && <p>{headline}</p>}

      <div className={styles.linkArrow}>
        <Link to={url}>{modelMessages.button}</Link>
      </div>
    </div>
  )
}
function getSlug(record: CardsInline): string | (string | null)[] | null {
  return record.__typename === 'DatoCmsGctPerson'
    ? [record.firstName, record.secondName]
    : record.slug
}

function getTopic(
  record: CardsInline
): Queries.TopicReferenceFragment | undefined | null {
  return record.__typename !== 'DatoCmsPctTopic' &&
    record.__typename !== 'DatoCmsPctInternalPage' &&
    record.__typename !== 'DatoCmsPctVision' &&
    record.__typename !== 'DatoCmsPctTag' &&
    record.__typename !== 'DatoCmsPctReport' &&
    record.__typename !== 'DatoCmsGctPerson'
    ? record.topic
    : undefined
}

function getExternalName(record: CardsInline): string | null | undefined {
  return record.__typename === 'DatoCmsGctPerson'
    ? `${record.firstName} ${record.secondName}`
    : record.externalName
}

function getHeadline(record: CardsInline): string | null {
  return record.__typename !== 'DatoCmsPctReport' ? record.headline : ''
}

export const query = graphql`
  fragment PctDialogCardInline on DatoCmsPctDialog {
    __typename
    id: originalId
    externalName
    headline: excerpt
    topic {
      ...TopicReference
    }
    slug
  }
  fragment PctTopicCardInline on DatoCmsPctTopic {
    __typename
    id: originalId
    externalName
    headline: description
    slug
  }
  fragment PctPaperCardInline on DatoCmsPctPaper {
    __typename
    id: originalId
    cover {
      alt
      gatsbyImageData(width: 600, layout: FULL_WIDTH)
    }
    externalName
    headline
    topic {
      ...TopicReference
    }
    slug
  }
  fragment PctInternalPageCardInline on DatoCmsPctInternalPage {
    __typename
    id: originalId
    externalName
    headline
    slug
  }

  fragment PctPostCardInline on DatoCmsPctPost {
    __typename
    id: originalId
    externalName
    headline
    topic {
      ...TopicReference
    }
    slug
  }

  fragment PctInterviewCardInline on DatoCmsPctInterview {
    __typename
    id: originalId
    externalName
    headline
    topic {
      ...TopicReference
    }
    slug
  }

  fragment PctProjectCardInline on DatoCmsPctProject {
    __typename

    id: originalId
    externalName
    headline: excerpt
    slug
    topic {
      ...TopicReference
    }
  }
  fragment PctPageCardInline on DatoCmsPctPage {
    __typename
    id: originalId
    externalName
    headline
    slug
    topic {
      ...TopicReference
    }
  }
  fragment PctVisionCardInline on DatoCmsPctVision {
    __typename
    id: originalId
    externalName
    headline
    slug
  }

  fragment PctTagCardInline on DatoCmsPctTag {
    __typename
    id: originalId
    externalName
    headline: description
    slug
  }

  fragment PctReportCardInline on DatoCmsPctReport {
    __typename
    id: originalId
    externalName
    slug
  }

  fragment PctEpisodeCardInline on DatoCmsPctEpisode {
    __typename
    id: originalId
    externalName
    headline: excerpt
    topic {
      ...TopicReference
    }
    slug
  }

  fragment PctReportPersonInline on DatoCmsGctPerson {
    __typename
    id: originalId
    firstName
    secondName
    headline: job
  }
`

export default CardInline
